import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Heart, TrendingUp, ChevronRight, Music, Zap, Calendar, Infinity } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReleaseModal from "../components/ReleaseModal";
import { CoverImage } from "../components/ReleaseCard";
import VoteButton from "../components/VoteButton";
import HypeButton from "../components/HypeButton";
import api from "../utils/api";

const RANK_STYLES = { 1: "text-yellow-400", 2: "text-zinc-300", 3: "text-amber-600" };
const RANK_BG    = {
  1: "border-yellow-400/30 bg-yellow-400/5",
  2: "border-zinc-400/20 bg-zinc-400/5",
  3: "border-amber-600/20 bg-amber-600/5",
};

function ChartEntry({ release, rank, onOpen, updateVote }) {
  const isTop3 = rank <= 3;
  return (
    <div
      key={release.id}
      data-testid={`chart-entry-${rank}`}
      className={`group flex items-center gap-4 p-3 rounded-2xl border transition-all duration-200 cursor-pointer hover:bg-zinc-900/80 ${
        isTop3 ? RANK_BG[rank] : "border-white/5 bg-zinc-900/30 hover:border-white/10"
      }`}
      onClick={() => onOpen(release)}
    >
      <div className={`w-10 text-center flex-shrink-0 font-black text-2xl tabular-nums ${isTop3 ? RANK_STYLES[rank] : "text-zinc-700"}`}>
        {rank}
      </div>
      <div className="relative w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-zinc-800">
        <CoverImage
          cover_url={release.cover_url}
          interpret={release.interpret}
          titel={release.titel}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-white text-sm truncate leading-tight group-hover:text-rose-300 transition-colors">{release.interpret}</p>
        <p className="text-zinc-400 text-xs truncate">{release.titel}</p>
        <div className="flex items-center gap-2 mt-1">
          {release.typ && <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 font-medium">{release.typ}</span>}
          {release.erscheinungsdatum && <span className="text-zinc-600 text-xs">{release.erscheinungsdatum.slice(0, 4)}</span>}
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <VoteButton
          release={release}
          size={17}
          showCount
          className="px-2 py-1"
          onVoteChange={(data) => updateVote(release.id, data)}
        />
      </div>
      <ChevronRight size={14} className="text-zinc-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

function EmptyChart({ type }) {
  const { t } = useTranslation();
  return (
    <div className="text-center py-16">
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-zinc-900 flex items-center justify-center">
        <Music size={22} className="text-zinc-600" />
      </div>
      <p className="text-zinc-500 text-sm">
        {type === "monthly" ? t("charts.no_votes_monthly") : t("charts.no_votes_alltime")}
      </p>
      <p className="text-zinc-600 text-xs mt-1">{t("charts.vote_hint")}</p>
      <Link to="/search" className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl transition-colors">
        {t("charts.discover_releases")} <ChevronRight size={14} />
      </Link>
    </div>
  );
}

export default function ChartsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("monthly");
  const [monthlyReleases, setMonthlyReleases] = useState([]);
  const [alltimeReleases, setAlltimeReleases] = useState([]);
  const [loadingMonthly, setLoadingMonthly] = useState(true);
  const [loadingAlltime, setLoadingAlltime] = useState(false);
  const [hypeReleases, setHypeReleases] = useState([]);
  const [hypeLoading, setHypeLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // Load monthly chart immediately
    api.get("/votes/top/monthly?limit=10")
      .then((r) => setMonthlyReleases(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingMonthly(false));
    // Load hype
    api.get("/hype/top?limit=3")
      .then((r) => setHypeReleases(r.data || []))
      .catch(() => {})
      .finally(() => setHypeLoading(false));
  }, []);

  useEffect(() => {
    // Load all-time on tab switch
    if (activeTab === "alltime" && alltimeReleases.length === 0) {
      setLoadingAlltime(true);
      api.get("/votes/top/alltime?limit=10")
        .then((r) => setAlltimeReleases(r.data || []))
        .catch(() => {})
        .finally(() => setLoadingAlltime(false));
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMonthly = (id, data) =>
    setMonthlyReleases((p) => p.map((r) => r.id === id ? { ...r, vote_count: data.vote_count } : r));
  const updateAlltime = (id, data) =>
    setAlltimeReleases((p) => p.map((r) => r.id === id ? { ...r, vote_count: data.vote_count } : r));

  const handleOpen = useCallback(async (release) => {
    try {
      const res = await api.get(`/releases/by-id/${release.id}`);
      setSelected(res.data);
    } catch {
      setSelected(release);
    }
  }, []);

  const currentMonth = new Date().toLocaleString("de-DE", { month: "long", year: "numeric" });

  return (
    <>
      <Helmet>
        <title>Charts – Top Releases | HUWND</title>
        <meta name="description" content="Die meistgevoteten Hands-Up Releases – monatlich und all-time. Stimme für deine Lieblingsreleases ab." />
        <link rel="canonical" href="https://handsupwillneverdie.de/charts" />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Charts – Top Releases | HUWND" />
        <meta property="og:description" content="Die meistgevoteten Hands-Up Releases – monatlich und all-time." />
        <meta property="og:url" content="https://handsupwillneverdie.de/charts" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Charts – Top Releases | HUWND" />
        <meta name="twitter:description" content="Die meistgevoteten Hands-Up Releases – monatlich und all-time." />
      </Helmet>
      <div className="min-h-screen bg-zinc-950 text-white">
        <Header />

        {/* Hero */}
        <section className="relative py-14 px-4 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-rose-600/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-rose-800/8 rounded-full blur-3xl" />
          </div>
          <div className="max-w-3xl mx-auto text-center relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-600/15 border border-rose-500/20 text-rose-400 text-xs font-semibold uppercase tracking-widest mb-5">
              <TrendingUp size={12} />
              Community Voting
            </div>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tight mb-4">
              Top 10 <span className="text-rose-500">Charts</span>
            </h1>
            <p className="text-zinc-400 text-base max-w-lg mx-auto">
              Logge dich ein und stimme für deine Lieblingsreleases.
              Monatlicher Reset am 1. des Monats.
            </p>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-4 pb-20">
          {/* ── HYPE SECTION ── */}
          {(hypeLoading || hypeReleases.length > 0) && (
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <Zap size={15} className="text-amber-400" fill="currentColor" />
                <span className="text-amber-400 text-xs font-bold uppercase tracking-widest">Supporter Hype</span>
                <span className="text-zinc-700 text-xs">· Diesen Monat · Top 3</span>
              </div>
              {hypeLoading ? (
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map(i => <div key={i} className="h-32 bg-zinc-900 rounded-2xl animate-pulse" />)}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {hypeReleases.map((r, i) => {
                    const rank = i + 1;
                    const rankColors = { 1: "text-yellow-400", 2: "text-zinc-400", 3: "text-amber-600" };
                    return (
                      <div
                        key={r.id}
                        data-testid={`hype-chart-entry-${rank}`}
                        onClick={() => handleOpen(r)}
                        className="group relative rounded-2xl overflow-hidden cursor-pointer border border-amber-500/10 hover:border-amber-500/30 transition-all bg-zinc-900/50"
                      >
                        <div className="aspect-square relative overflow-hidden">
                          <CoverImage cover_url={r.cover_url} interpret={r.interpret} titel={r.titel}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                          <span className={`absolute top-2 left-2 font-black text-xl ${rankColors[rank]}`}>#{rank}</span>
                          <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded-full px-1.5 py-0.5">
                            <Zap size={9} className="text-amber-400" fill="currentColor" />
                            <span className="text-amber-400 text-xs font-bold">{r.hype_count}</span>
                          </div>
                        </div>
                        <div className="p-2">
                          <p className="text-white text-xs font-bold truncate group-hover:text-amber-300 transition-colors">{r.interpret}</p>
                          <p className="text-zinc-500 text-xs truncate">{r.titel}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="border-t border-white/5 mt-8" />
            </div>
          )}

          {/* ── TABS ── */}
          <div className="flex items-center gap-1 p-1 bg-zinc-900 rounded-xl mb-6 w-full">
            <button
              data-testid="tab-monthly"
              onClick={() => setActiveTab("monthly")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-bold transition-all ${
                activeTab === "monthly"
                  ? "bg-rose-600 text-white shadow-md"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              <Calendar size={14} />
              {t("charts.monthly_tab")}
            </button>
            <button
              data-testid="tab-alltime"
              onClick={() => setActiveTab("alltime")}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-bold transition-all ${
                activeTab === "alltime"
                  ? "bg-rose-600 text-white shadow-md"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              <Infinity size={14} />
              {t("charts.alltime_tab")}
            </button>
          </div>

          {/* Monthly Chart */}
          {activeTab === "monthly" && (
            loadingMonthly ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-24 bg-zinc-900 rounded-2xl animate-pulse" />)}
              </div>
            ) : monthlyReleases.length === 0 ? (
              <EmptyChart type="monthly" />
            ) : (
              <div className="space-y-2" data-testid="monthly-chart-list">
                {monthlyReleases.map((release, i) => (
                  <ChartEntry key={release.id} release={release} rank={i + 1} onOpen={handleOpen} updateVote={updateMonthly} />
                ))}
              </div>
            )
          )}

          {/* All Time Chart */}
          {activeTab === "alltime" && (
            loadingAlltime ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-24 bg-zinc-900 rounded-2xl animate-pulse" />)}
              </div>
            ) : alltimeReleases.length === 0 ? (
              <EmptyChart type="alltime" />
            ) : (
              <div className="space-y-2" data-testid="alltime-chart-list">
                {alltimeReleases.map((release, i) => (
                  <ChartEntry key={release.id} release={release} rank={i + 1} onOpen={handleOpen} updateVote={updateAlltime} />
                ))}
              </div>
            )
          )}

          {/* CTA */}
          <div className="mt-10 rounded-2xl border border-rose-500/20 bg-rose-600/8 p-6 text-center">
            <p className="text-white font-bold mb-1">{t("charts.vote_hint")}</p>
            <p className="text-zinc-400 text-sm mb-4">{t("charts.chart_updated")}</p>
            <Link to="/search" className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl transition-colors" data-testid="charts-cta-browse">
              {t("charts.discover_releases")} <ChevronRight size={14} />
            </Link>
          </div>
        </section>

        <Footer />
      </div>

      {selected && <ReleaseModal release={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
